<template>
  <LISADialog
    v-if="isLISA3"
    v-model="visibleProp"
    :draggable="draggable"
    :size="size"
    :content-padding="contentPadding"
    :close-on-backdrop="!noCloseOnBackdrop"
    @cancel="handleLISADialogCancel"
    @show="handleShow"
  >
    <template v-if="'title-prepend' in $slots" #title-prepend>
      <slot name="title-prepend" />
    </template>
    <template #title>
      <slot name="title">
        {{ title }}
      </slot></template
    >
    <template #default>
      <form
        v-if="visibleProp"
        ref="submitElement"
        novalidate
        :class="formClass"
        :data-testid="`${testName}-form`"
        @submit.prevent="submitForm"
      >
        <slot name="default" :validation="validation" :submit="submitForm">
          <div v-if="loading" class="flex justify-content-center">
            <ProgressSpinner />
          </div>
        </slot>
        <LISAButton type="submit" style="display: none" />
      </form>
    </template>
    <template v-if="showFooter === true" #footer>
      <div class="lisaui:flex lisaui:flex-1">
        <slot name="footer:prepend"> </slot>
      </div>
      <ValidationSummaryIndicator
        v-if="!hideValidationSummary && isSpecialAdmin()"
        :validation="validation"
        :extra-validation-rules="extraValidationRules"
      />
      <slot
        name="footer"
        :validation="validation"
        :disabled="loading || disabled"
        :ok-disabled="okDisabled"
        :close="handleLISADialogCancel"
        :hide="handleLISADialogCancel"
        :ok="submitForm"
      >
        <slot
          name="footerExtra"
          :validation="validation"
          :ok-disabled="okDisabled"
          :disabled="submitDisabled"
        />
        <LISAButton
          v-if="!okOnly"
          :disabled="disabled"
          severity="secondary"
          @click="handleLISADialogCancel"
        >
          <slot name="modal-cancel" :disabled="disabled">
            <i class="fas fa-times mr-1" />{{ t("global.cancel") }}
          </slot>
        </LISAButton>
        <slot name="modal-ok-button" :validation="validation" :ok="submitForm">
          <LISAButton
            v-if="!hideSave"
            :data-testid="okTestId"
            :severity="okVariant"
            :disabled="submitDisabled"
            @click="submitForm"
          >
            <slot name="modal-ok" :disabled="submitDisabled" :validation="validation">
              <i class="fas fa-save mr-1" />{{ t("global.save") }}
            </slot>
          </LISAButton>
        </slot>
      </slot>
    </template>
  </LISADialog>
  <Dialog
    v-else
    ref="dialog"
    v-model:visible="visibleProp"
    v-bind="$attrs"
    :class="{ coffeeStainModal: showCoffeeStain }"
    :content-style="modalWidth"
    :style="modalStyle"
    :breakpoints="breakpoints"
    modal
    :dismissable-mask="!noCloseOnBackdrop"
    maximizable
    :keep-in-viewport="false"
    :header="title"
    @show="handleShow"
  >
    <template v-if="showFooter" #footer>
      <div class="pt-2 footer" :data-testid="`${testName}-footer`">
        <slot
          name="footer"
          :validation="validation"
          :disabled="loading || disabled"
          :ok-disabled="okDisabled"
          :close="close"
          :hide="close"
          :ok="submitForm"
        >
          <slot
            name="footerExtra"
            :validation="validation"
            :ok-disabled="okDisabled"
            :disabled="submitDisabled"
          />
          <ValidationSummaryIndicator
            v-if="!hideValidationSummary && isModifierSet(ModifierType.LISA3)"
            :validation="validation"
            :extra-validation-rules="extraValidationRules"
            class="mr-auto"
          />
          <LISAButton v-if="!okOnly" :disabled="disabled" severity="secondary" @click="close">
            <slot name="modal-cancel" :disabled="disabled">
              <i class="fas fa-times mr-1" />{{ t("global.cancel") }}
            </slot>
          </LISAButton>
          <slot name="modal-ok-button" :validation="validation" :ok="submitForm">
            <LISAButton
              v-if="!hideSave"
              :data-testid="okTestId"
              :severity="okVariant"
              :disabled="submitDisabled"
              @click="submitForm"
            >
              <slot name="modal-ok" :disabled="submitDisabled" :validation="validation">
                <i class="fas fa-save mr-1" />{{ t("global.save") }}
              </slot>
            </LISAButton>
          </slot>
        </slot>
      </div>
    </template>

    <template #header>
      <slot name="header" :validation="validation">
        <h2>{{ title }}</h2>
      </slot>
    </template>

    <template #default>
      <form
        ref="submitElement"
        novalidate
        :class="formClass"
        :data-testid="`${testName}-form`"
        @submit.prevent="submitForm"
      >
        <slot name="default" :validation="validation" :submit="submitForm">
          <div v-if="loading" class="flex justify-content-center">
            <ProgressSpinner />
          </div>
        </slot>
        <LISAButton type="submit" style="display: none" />
      </form>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useTranslation } from "@/language/translation";
import Dialog from "primevue/dialog";
import ProgressSpinner from "primevue/progressspinner";
import LISAButton from "@LISAPrime/LISAButton.vue";
import { confirmDiscardChanges, useUnsavedChanges } from "@/functions/useUnsavedChanges";
import isModifierSet from "@/functions/userModifier";
import { ModifierType } from "@/functions/userModifierEnum";
import { type SubmitHandler, useFormValidation } from "@/modules/formValidation";
import ValidationSummaryIndicator from "@/components/shared/form/ValidationSummaryIndicator.vue";
import type { ExtraValidationRule } from "@/components/shared/form/ExtraValidationRule";
import { LISADialog } from "@fi-solutions/lisa-ui";
import { isLISA3 } from "@/composables/useLISA3";
import { isSpecialAdmin } from "@/functions/environment";

type ModalSize = "md" | "lg" | "xl" | "xxl";
type FormClass = string | Record<string, boolean>;

const props = withDefaults(
  defineProps<{
    title?: string;
    visible?: boolean;
    loading?: boolean;
    baselineLoading?: boolean;
    disabled?: boolean;
    okDisabled?: boolean;
    okVariant?: string;
    okTestId?: string;
    size?: ModalSize;
    minHeight?: string;
    showFooter?: boolean;
    okOnly?: boolean;
    hideSave?: boolean;
    propagateValidation?: boolean;
    noCloseOnBackdrop?: boolean;
    ignoreDiscardChangesConfirmation?: boolean;
    formClass?: FormClass;
    testName?: string;
    hideCoffe?: boolean;
    submit?: SubmitHandler;
    extraValidationRules?: readonly ExtraValidationRule[];
    hideValidationSummary?: boolean;
    contentPadding?: boolean;
    draggable?: boolean;
  }>(),
  {
    title: "",
    visible: false,
    loading: false,
    baselineLoading: undefined,
    disabled: false,
    okDisabled: false,
    okVariant: "primary",
    okTestId: "modal-button-submit",
    size: "md",
    minHeight: "unset",
    showFooter: true,
    okOnly: false,
    hideSave: false,
    propagateValidation: false,
    noCloseOnBackdrop: false,
    ignoreDiscardChangesConfirmation: false,
    formClass: "",
    testName: "LModal",
    hideCoffe: true,
    submit: undefined,
    extraValidationRules: () => [],
    hideValidationSummary: false,
    contentPadding: true,
    draggable: true,
  },
);

const emit = defineEmits<{
  discardChanges: [];
  "update:visible": [visible: boolean];
  show: [];
  abort: [];
  cancel: [];
  "observer:valid": [valid: boolean];
}>();

const { t } = useTranslation();
const baselineLoading = computed(() => props.baselineLoading ?? props.loading);
const validation = useFormValidation({
  name: () => `LModal:${props.testName}`,
  displayName: () => props.title,
  loading: baselineLoading,
  disabled: () => props.disabled,
  propagateValidation: () => props.propagateValidation,
});

const visibleProp = computed({
  get: () => props.visible,
  set: (value: boolean) => emit("update:visible", value),
});
const submitDisabled = computed(
  () =>
    props.loading ||
    props.disabled ||
    props.okDisabled ||
    !validation.canSubmit ||
    props.extraValidationRules.some((rule) => !rule.condition),
);
const showCoffeeStain = computed(() => isModifierSet(ModifierType.coffeeStain) && !props.hideCoffe);

useUnsavedChanges({
  isDirty: computed(
    () => !props.ignoreDiscardChangesConfirmation && visibleProp.value && validation.dirty,
  ),
  confirmationOptions: {
    message: t("components.event-action.TaskModal.confirmText"),
    header: t("global.confirm"),
    acceptLabel: t("global.yes"),
    rejectLabel: t("global.cancel"),
    icon: "pi pi-exclamation-triangle",
  },
});

watch(
  () => validation.valid,
  (valid) => emit("observer:valid", valid),
  { immediate: true },
);

type DialogController = InstanceType<typeof Dialog> & {
  close: (reason?: string | Event) => void;
  onMaskClick: (event: PointerEvent) => void;
};

const dialog = ref<DialogController>();

onMounted(() => {
  if (props.ignoreDiscardChangesConfirmation || dialog.value == null) {
    return;
  }

  dialog.value.onMaskClick = (event: PointerEvent) => {
    const activeElement = document.activeElement;
    if (
      !props.noCloseOnBackdrop &&
      activeElement?.tagName === "BODY" &&
      event.target instanceof HTMLDivElement &&
      event.target.classList.contains("p-dialog-mask")
    ) {
      dialog.value?.close();
    }
  };

  const originalClose = dialog.value.close.bind(dialog.value);
  dialog.value.close = async (reason?: string | Event) => {
    if (validation.dirty) {
      const discard = await confirmDiscardChanges({
        message: t("components.event-action.TaskModal.confirmText"),
        header: t("global.confirm"),
        acceptLabel: t("global.yes"),
        rejectLabel: t("global.cancel"),
        icon: "pi pi-exclamation-triangle",
      });
      if (!discard) {
        return;
      }
      await validation.resetAllToBaselines();
      emit("discardChanges");
    }

    if (reason === "abort") {
      emit("abort");
    } else {
      emit("cancel");
    }
    originalClose();
  };
});

watch(baselineLoading, async (isLoading, wasLoading) => {
  if (props.baselineLoading == null || !visibleProp.value || isLoading === wasLoading) {
    return;
  }

  if (isLoading) {
    await validation.beginBaselineSession();
    return;
  }

  // Loading can finish before v-if guarded fields mount and before the loaded model
  // has propagated through them. Establish the baseline after that render.
  await nextTick();
  await validation.beginBaselineSession();
});

async function submitForm() {
  if (submitDisabled.value) {
    return;
  }

  const result = await props.submit?.();
  if (props.submit != null && !result?.success) {
    return;
  }

  await validation.commitBaseline();
  if (result?.success) {
    await result?.afterCommit?.();
  }
  visibleProp.value = false;
}

async function handleShow() {
  emit("show");
  await nextTick();
  await validation.beginBaselineSession();
}

function close() {
  dialog.value?.close("abort");
}

function show() {
  visibleProp.value = true;
}

const breakpoints = computed((): Record<string, string> | undefined => {
  switch (props.size) {
    case "lg":
      return { "991px": "500px" };
    case "xl":
      return { "1199px": "800px", "991px": "500px" };
    case "xxl":
      return { "991px": "500px" };
    default:
      return undefined;
  }
});

const modalWidth = computed(() => `width: 100%; min-height: ${props.minHeight};`);
const modalStyle = computed(() => {
  switch (props.size) {
    case "lg":
      return "width: 800px;";
    case "xl":
      return "width: 1140px;";
    case "xxl":
      return "width: 90vw;";
    default:
      return "width: 500px;";
  }
});

defineExpose({ validation, show, close });

// LISADialog

async function handleLISADialogCancel(e?: Event) {
  e?.preventDefault();
  if (validation.dirty) {
    const discard = await confirmDiscardChanges({
      message: t("components.event-action.TaskModal.confirmText"),
      header: t("global.confirm"),
      acceptLabel: t("global.yes"),
      rejectLabel: t("global.cancel"),
      icon: "pi pi-exclamation-triangle",
    });
    if (!discard) {
      return;
    }
    await validation.resetAllToBaselines();
    emit("discardChanges");
  }
  visibleProp.value = false;
  emit("cancel");
}
</script>

<style lang="scss" scoped>
.footer {
  display: flex;
  align-items: end;

  > button {
    margin-left: 0.5rem;
  }
}

:global(.coffeeStainModal) {
  position: relative;
  overflow: hidden;
}

:global(.coffeeStainModal::before) {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 6;
  width: 94rem;
  height: 64rem;
  pointer-events: none;
  content: "";
  background-image: url("@/assets/coffeeStain.png");
  background-repeat: no-repeat;
  background-position: 0 79%;
  background-size: auto 145%;
  filter: saturate(-9.18) brightness(1.22) contrast(-1.08);
  mix-blend-mode: multiply;
  opacity: 0.48;
  transform: rotate(90deg);
  transform-origin: 54% 69%;
}
</style>
